import classNames from "classnames";
import React, { FunctionComponent } from "react";
import { CSS_CLASSES } from "./separator-css-classes";

export type SeparatorOrientationOption = "vertical" | "horizontal";

export type SeparatorGapOption = "none" | "xs" | "sm" | "md" | "lg" | "xl";

export interface SeparatorProps {
  /**
   * This prop control the orientation of the separator.
   *
   * When used between content that's arranged side-by-side inside a flex container that has it's `flex-direction`
   * set to `row` use the `vertical` option (default).
   *
   * When used between content that's arranged one below the other inside a flex container that has it's `flex-direction`
   * set to `column` use the `horizontal` option.
   *
   * @default vertical
   */
  orientation?: SeparatorOrientationOption;

  /**
   * This props controls the gap between the content and the Separator, on either side of it.
   * If the gap required i not achievable by the built in options, the set this to `none` and use `className` to add
   * appropriate `margin` to achieve custom gap.
   *
   * @default md
   */
  gap?: SeparatorGapOption;

  /**
   * This prop can be used to pass custom styling class names to this component.
   * Use this to add margin or padding to the separator to suite the layout you're using it it.
   */
  className?: string;
}

export const Separator: FunctionComponent<SeparatorProps> = ({
  orientation = "vertical",
  gap = "md",
  className,
  ...restProps
}) => {
  const separatorClassName = classNames(
    CSS_CLASSES.SEPARATOR_ROOT,
    {
      [CSS_CLASSES.SEPARATOR_VERTICAL]: orientation === "vertical",
      [CSS_CLASSES.SEPARATOR_HORIZONTAL]: orientation === "horizontal",

      [CSS_CLASSES.SEPARATOR_VERTICAL_GAP_XS]: orientation === "vertical" && gap === "xs",
      [CSS_CLASSES.SEPARATOR_VERTICAL_GAP_SM]: orientation === "vertical" && gap === "sm",
      [CSS_CLASSES.SEPARATOR_VERTICAL_GAP_MD]: orientation === "vertical" && gap === "md",
      [CSS_CLASSES.SEPARATOR_VERTICAL_GAP_LG]: orientation === "vertical" && gap === "lg",
      [CSS_CLASSES.SEPARATOR_VERTICAL_GAP_XL]: orientation === "vertical" && gap === "xl",

      [CSS_CLASSES.SEPARATOR_HORIZONTAL_GAP_XS]: orientation === "horizontal" && gap === "xs",
      [CSS_CLASSES.SEPARATOR_HORIZONTAL_GAP_SM]: orientation === "horizontal" && gap === "sm",
      [CSS_CLASSES.SEPARATOR_HORIZONTAL_GAP_MD]: orientation === "horizontal" && gap === "md",
      [CSS_CLASSES.SEPARATOR_HORIZONTAL_GAP_LG]: orientation === "horizontal" && gap === "lg",
      [CSS_CLASSES.SEPARATOR_HORIZONTAL_GAP_XL]: orientation === "horizontal" && gap === "xl",
    },
    className
  );

  return <div className={separatorClassName} {...restProps} />;
};
